import { distanceXY } from '@geometry'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { lineIsXCartesian, lineIsYCartesian } from 'src/geometry/checks/checkCartesian'
import { NUM_DECIMALS_ROUND_FLOATS, roundFloatToDecimalPlaces } from 'src/geometry/floatHacks'

export type WidthAnchorMode = 'start' | 'middle' | 'end'

export interface ChangeOpeningWidthCartesianAwareResult {
  newStart: ImmutableVector3
  newEnd: ImmutableVector3
  newHeight: number
}

interface PointLike {
  x: number
  y: number
  z: number
}

export const changeOpeningWidthCartesianAware = (
  openingStart: PointLike,
  openingEnd: PointLike,
  newWidthAsNumber: number,
  anchorMode: WidthAnchorMode,
): ChangeOpeningWidthCartesianAwareResult => {
  // the form stores these points as plain objects, so construct real vectors
  // before calling any vector methods on them
  const globalStart = new ImmutableVector3(openingStart.x, openingStart.y, openingStart.z)
  const globalEnd = new ImmutableVector3(openingEnd.x, openingEnd.y, openingEnd.z)

  const currentStart = globalStart
  const currentEnd = globalEnd

  const currentWidth = distanceXY(globalStart, globalEnd)
  if (currentWidth === 0) {
    return {
      newStart: currentStart,
      newEnd: currentEnd,
      newHeight: currentEnd.z - currentStart.z,
    }
  }

  const globalStartXY = new ImmutableVector3(globalStart.x, globalStart.y, 0)
  const globalEndXY = new ImmutableVector3(globalEnd.x, globalEnd.y, 0)
  const direction = globalEndXY.sub(globalStartXY).normalize()
  const directionSignX = direction.x > 0 ? 1 : -1
  const directionSignY = direction.y > 0 ? 1 : -1

  let newStart = globalStart
  let newEnd = globalEnd

  const openingIsXCartesian = lineIsXCartesian(globalStart, globalEnd)
  const openingIsYCartesian = lineIsYCartesian(globalStart, globalEnd)

  if (anchorMode === 'start') {
    if (openingIsXCartesian) {
      newEnd = new ImmutableVector3(
        globalStart.x + newWidthAsNumber * directionSignX,
        globalStart.y,
        currentEnd.z,
      )
    } else if (openingIsYCartesian) {
      newEnd = new ImmutableVector3(
        globalStart.x,
        globalStart.y + newWidthAsNumber * directionSignY,
        currentEnd.z,
      )
    } else {
      const newEndBase = currentStart.addScaledVector(direction, newWidthAsNumber)
      newEnd = new ImmutableVector3(newEndBase.x, newEndBase.y, currentEnd.z)
    }
  } else if (anchorMode === 'middle') {
    const halfWidth = newWidthAsNumber / 2
    const center = currentStart.add(currentEnd).divideScalar(2)
    if (openingIsXCartesian) {
      newStart = new ImmutableVector3(center.x - halfWidth, currentStart.y, currentStart.z)
      newEnd = new ImmutableVector3(center.x + halfWidth, currentStart.y, currentEnd.z)
    } else if (openingIsYCartesian) {
      newStart = new ImmutableVector3(currentStart.x, center.y - halfWidth, currentStart.z)
      newEnd = new ImmutableVector3(currentStart.x, center.y + halfWidth, currentEnd.z)
    } else {
      const newStartXY = center.addScaledVector(direction, (newWidthAsNumber / 2) * -1)
      const newEndXY = center.addScaledVector(direction, newWidthAsNumber / 2)
      newStart = new ImmutableVector3(newStartXY.x, newStartXY.y, currentStart.z)
      newEnd = new ImmutableVector3(newEndXY.x, newEndXY.y, currentEnd.z)
    }
  } else if (anchorMode === 'end') {
    if (openingIsXCartesian) {
      newStart = new ImmutableVector3(
        globalEnd.x - newWidthAsNumber * directionSignX,
        globalEnd.y,
        currentStart.z,
      )
    } else if (openingIsYCartesian) {
      newStart = new ImmutableVector3(
        globalEnd.x,
        globalEnd.y - newWidthAsNumber * directionSignY,
        currentStart.z,
      )
    } else {
      const newStartBase = currentEnd.addScaledVector(direction, newWidthAsNumber * -1)
      newStart = new ImmutableVector3(newStartBase.x, newStartBase.y, currentStart.z)
    }
  }
  const finalNewStart = new ImmutableVector3(
    roundFloatToDecimalPlaces(newStart.x, NUM_DECIMALS_ROUND_FLOATS),
    roundFloatToDecimalPlaces(newStart.y, NUM_DECIMALS_ROUND_FLOATS),
    roundFloatToDecimalPlaces(currentStart.z, NUM_DECIMALS_ROUND_FLOATS),
  )
  const finalNewEnd = new ImmutableVector3(
    roundFloatToDecimalPlaces(newEnd.x, NUM_DECIMALS_ROUND_FLOATS),
    roundFloatToDecimalPlaces(newEnd.y, NUM_DECIMALS_ROUND_FLOATS),
    roundFloatToDecimalPlaces(currentEnd.z, NUM_DECIMALS_ROUND_FLOATS),
  )
  const newHeight = roundFloatToDecimalPlaces(
    finalNewEnd.z - finalNewStart.z,
    NUM_DECIMALS_ROUND_FLOATS,
  )
  return {
    newStart: finalNewStart,
    newEnd: finalNewEnd,
    newHeight,
  }
}
